import { Dialog, Transition, Switch, RadioGroup } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { ExclamationIcon, StarIcon } from "@heroicons/react/solid";
import { Fragment, ReactNode, useState, useCallback, useRef } from "react";
import PrimaryButton from "../buttons/primary";
import ErrorCallout from "../callouts/error";
import TextInput from "../input/text";
import NeutralModal from "./neutral";
import { toast } from "react-hot-toast";
import { useAuth } from "../../hooks/auth";
import { useApi } from "../../hooks/api";
import { PrepCenter, SupportedModel, Turnaround } from "../../model/prepCenter";
import classNames from "../../util/classNames";

export default function CreateReviewModal({
  open,
  setOpen,
  prepCenterId,
  onCreate,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  prepCenterId: string | null;
  onCreate?: (prepCenter?: PrepCenter) => void;
}) {
  const formRef: React.RefObject<HTMLFormElement> = useRef(null);
  const { authUser, dbUser, prepCenter, updateUser } = useAuth();
  const { createReview } = useApi();
  const [fieldsRequired, setFieldsRequired] = useState(false);
  const [fieldValidity, setFieldValidity] = useState<{
    [key: string]: boolean;
  }>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");

  const handleClear = useCallback(async () => {
    // Reset fields
    setRating(0);
    setReview("");

    // Reset required field validation
    setFieldsRequired(false);
    setFieldValidity({});
  }, [setRating, setReview, setFieldsRequired, setFieldValidity]);

  const handleModal = useCallback(async () => {
    if (!prepCenterId) return;

    if (!rating) {
      setFieldsRequired(true);
      formRef?.current?.scrollIntoView({ behavior: "smooth" });
      throw new Error("Rating is a required field.");
    }
    if (Object.values(fieldValidity).includes(false)) {
      formRef?.current?.scrollIntoView({ behavior: "smooth" });
      throw new Error(
        `Invalid fields: ${Object.entries(fieldValidity)
          .filter(([_, v]) => !v)
          .map(([k, _]) => k)
          .join(", ")}.`
      );
    }

    let { errorMessage } = await createReview({
      prep_center_id: prepCenterId as string,
      rating,
      review,
    });
    if (errorMessage) {
      toast(`Error: ${errorMessage}`);
      formRef?.current?.scrollIntoView({ behavior: "smooth" });
      throw new Error(errorMessage);
    } else {
      toast("Added review.");
      if (onCreate) onCreate();
    }
  }, [prepCenterId, rating, review, fieldValidity, createReview, onCreate]);

  return (
    authUser && (
      <NeutralModal
        open={open}
        setOpen={setOpen}
        title="Write a review"
        description="How was your experience with this prep center?"
        saveText="Submit"
        handle={handleModal}
        clear={handleClear}
      >
        <code className="hidden text-gray-700 whitespace-pre">{`
        rating: ${JSON.stringify(rating)}
        review: ${JSON.stringify(review)}
        -
        fieldValidity: ${JSON.stringify(fieldValidity)}
      `}</code>

        <form className="space-y-8 divide-y divide-gray-200" ref={formRef}>
          <div className="space-y-8 divide-y divide-gray-200">
            <div>
              <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <RadioGroup
                  value={rating}
                  onChange={setRating}
                  className="m:col-span-6"
                >
                  <RadioGroup.Label className="block text-sm font-medium text-gray-700">
                    Rating
                  </RadioGroup.Label>

                  <div className="mt-2 flex items-center space-x-2">
                    {[0, 1, 2, 3, 4].map((r) => (
                      <RadioGroup.Option
                        key={r}
                        value={r + 1}
                        className="cursor-pointer"
                      >
                        <RadioGroup.Label as="span" className="sr-only">
                          {r} out of 5 stars
                        </RadioGroup.Label>
                        <StarIcon
                          className={classNames(
                            rating > r ? "text-yellow-400" : "text-gray-300",
                            "h-8 w-8 flex-shrink-0"
                          )}
                          aria-hidden="true"
                        />
                      </RadioGroup.Option>
                    ))}
                  </div>

                  <p className="sr-only">{rating} out of 5 stars</p>
                </RadioGroup>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="review"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Review
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="review"
                      name="review"
                      rows={6}
                      value={review}
                      onChange={(e) => setReview(e.target.value)}
                      placeholder="(Optional) Leave a detailed review to help other sellers."
                      className="shadow-sm focus:ring-vendrive-500 focus:border-vendrive-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </NeutralModal>
    )
  );
}
